import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Search, ArrowUpDown } from 'lucide-react';

import Input from '../components/Input';
import Button from '../components/Button';
import Loading from '../components/Loading';
import { addVerificationProcess, getVerificationProcesses, updateVerificationProcess, deleteVerificationProcess } from '../actions/verificationProcessActions';
import {
    ADD_VERIFICATION_PROCESS_RESET,
    GET_VERIFICATION_PROCESSES_RESET,
    UPDATE_VERIFICATION_PROCESS_RESET,
    DELETE_VERIFICATION_PROCESS_RESET,
} from '../constants/verificationProcessConstants';

const VerificationProcessList = () => {
    // States - following ProcessList structure
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('process');
    const [sortDirection, setSortDirection] = useState('asc');
    const [editingVerificationProcess, setEditingVerificationProcess] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);

    // Form states
    const [process, setProcess] = useState('');

    const dispatch = useDispatch();

    // Check for auth - following ProcessList structure
    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        if (!userInfo) {
            // Redirect to login if needed
        }
    }, [userInfo]);

    useEffect(() => {
        dispatch(getVerificationProcesses(currentPage, 10, searchTerm));
    }, [dispatch, searchTerm, currentPage]);

    // Get Verification Processes - following ProcessList structure exactly
    const getVerificationProcessesInfo = useSelector((state) => state.getVerificationProcessesInfo);
    const { loadingGetVerificationProcesses, errorGetVerificationProcesses, getVerificationProcessesData } = getVerificationProcessesInfo;

    useEffect(() => {
        dispatch({ type: GET_VERIFICATION_PROCESSES_RESET });
        if (getVerificationProcessesData) {
            setLoading(false);
            setData(getVerificationProcessesData.data); // Copy to local state like ProcessList
            setTotalPages(getVerificationProcessesData.pages);
        } else if (errorGetVerificationProcesses) {
            setLoading(false);
            toast(errorGetVerificationProcesses, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [getVerificationProcessesData, errorGetVerificationProcesses]);

    // Add Verification Process - following ProcessList structure
    const addVerificationProcessInfo = useSelector((state) => state.addVerificationProcessInfo);
    const { loadingAddVerificationProcess, errorAddVerificationProcess, addVerificationProcessData } = addVerificationProcessInfo;

    useEffect(() => {
        dispatch({ type: ADD_VERIFICATION_PROCESS_RESET });
        if (addVerificationProcessData) {
            toast('Verification Process added successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            closeModal();
            setTimeout(() => {
                dispatch(getVerificationProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorAddVerificationProcess) {
            toast(errorAddVerificationProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [addVerificationProcessData, errorAddVerificationProcess]);

    // Update Verification Process - following ProcessList structure
    const updateVerificationProcessInfo = useSelector((state) => state.updateVerificationProcessInfo);
    const { loadingUpdateVerificationProcess, errorUpdateVerificationProcess, updateVerificationProcessData } = updateVerificationProcessInfo;

    useEffect(() => {
        dispatch({ type: UPDATE_VERIFICATION_PROCESS_RESET });
        if (updateVerificationProcessData) {
            toast('Verification Process updated successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            closeModal();
            setTimeout(() => {
                dispatch(getVerificationProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorUpdateVerificationProcess) {
            toast(errorUpdateVerificationProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [updateVerificationProcessData, errorUpdateVerificationProcess]);

    // Delete Verification Process - following ProcessList structure
    const deleteVerificationProcessInfo = useSelector((state) => state.deleteVerificationProcessInfo);
    const { loadingDeleteVerificationProcess, errorDeleteVerificationProcess, deleteVerificationProcessData } = deleteVerificationProcessInfo;

    useEffect(() => {
        dispatch({ type: DELETE_VERIFICATION_PROCESS_RESET });
        if (deleteVerificationProcessData) {
            toast('Verification Process deleted successfully', {
                type: 'success',
                hideProgressBar: true,
                autoClose: 2000,
            });
            setTimeout(() => {
                dispatch(getVerificationProcesses(currentPage, 10, searchTerm));
            }, 1000);
        } else if (errorDeleteVerificationProcess) {
            toast(errorDeleteVerificationProcess, {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
        }
    }, [deleteVerificationProcessData, errorDeleteVerificationProcess]);

    // Modal handlers - following ProcessList structure
    const openModal = () => {
        setEditingVerificationProcess(null);
        setProcess('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setProcess('');
        setEditingVerificationProcess(null);
    };

    // Submit handler - following ProcessList structure
    const handleSubmit = () => {
        if (!process) {
            toast('Process field is mandatory', {
                type: 'error',
                hideProgressBar: true,
                autoClose: 2000,
            });
            return;
        }

        if (editingVerificationProcess) {
            dispatch(updateVerificationProcess(editingVerificationProcess._id, process));
        } else {
            dispatch(addVerificationProcess(process));
        }
    };

    const handleDeleteVerificationProcess = (id) => {
        if (window.confirm('Are you sure you want to delete this verification process?')) {
            dispatch(deleteVerificationProcess(id));
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const openEditModal = (verificationProcessData) => {
        setEditingVerificationProcess(verificationProcessData);
        setProcess(verificationProcessData.process);
        setShowModal(true);
    };

    if (loading) {
        return <Loading />;
    }

    // Use local state data like ProcessList does
    const sortedVerificationProcesses = [...data].sort((a, b) => {
        const fieldA = (a[sortField] || '').toLowerCase();
        const fieldB = (b[sortField] || '').toLowerCase();
        if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
        if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="w-full h-full">
            <h1 className="text-2xl font-semibold">Verification Process Management</h1>
            <div className="bg-white shadow-md rounded px-8 py-4 my-4">
                <div className="flex justify-between">
                    <div className="flex gap-5">
                        <Input
                            name="Search Verification Process"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-3">
                        <Button
                            type="button"
                            onClick={openModal}
                            text="Add Verification Process"
                        />
                    </div>
                </div>
            </div>

            <div className="bg-white shadow-md rounded">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th
                                    onClick={() => handleSort('process')}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                                >
                                    <div className="flex items-center space-x-1">
                                        <span>Process Name</span>
                                        <ArrowUpDown size={14} className="text-gray-400" />
                                    </div>
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedVerificationProcesses.length > 0 ? (
                                sortedVerificationProcesses.map((verificationProcessItem) => (
                                    <tr key={verificationProcessItem._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                                                    <span className="text-green-600 font-medium">
                                                        {verificationProcessItem.process ? verificationProcessItem.process.charAt(0).toUpperCase() : '?'}
                                                    </span>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{verificationProcessItem.process}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                onClick={() => openEditModal(verificationProcessItem)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="text-red-600 hover:text-red-900"
                                                onClick={() => handleDeleteVerificationProcess(verificationProcessItem._id)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="2" className="px-6 py-12 text-center">
                                        <div className="text-gray-500">No verification processes found</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {sortedVerificationProcesses.length > 0 && (
                        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                            Showing {sortedVerificationProcesses.length} of {data.length} verification processes
                        </div>
                    )}
                </div>
            </div>

            {/* Modal - following ProcessList structure exactly */}
            {showModal && (
                <>
                    <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-auto my-6 mx-auto max-w-3xl mt-6">
                            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                <div className="flex items-center justify-between py-3 px-3 border-b border-solid border-blueGray-200 rounded-t">
                                    <h3 className="text-lg font-semibold">
                                        {editingVerificationProcess ? 'Edit Verification Process' : 'Add Verification Process'}
                                    </h3>
                                </div>
                                <div className="relative p-6 flex-auto">
                                    <Input
                                        width="w-full mb-4"
                                        name="Process Name *"
                                        value={process}
                                        onChange={(e) => setProcess(e.target.value)}
                                    />
                                    <p className="text-sm">All the fields with * are mandatory</p>
                                </div>
                                <div className="flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b">
                                    <button
                                        className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                                        type="button"
                                        onClick={closeModal}
                                    >
                                        Close
                                    </button>
                                    <button
                                        className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300"
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={loadingAddVerificationProcess || loadingUpdateVerificationProcess}
                                    >
                                        {editingVerificationProcess ? 'Update' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-4">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded border ${currentPage === 1 ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Prev
                    </button>

                    {[...Array(totalPages)].map((_, i) => {
                        const page = i + 1;
                        return (
                            <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded border ${page === currentPage ? 'bg-blue-600 text-white' : 'bg-white hover:bg-gray-100'
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded border ${currentPage === totalPages ? 'bg-gray-200 cursor-not-allowed' : 'bg-white hover:bg-gray-100'
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default VerificationProcessList;